import React, { useState } from "react";
import PolicyLifecyclePanel, { POLICY_STATUS_CFG } from "./PolicyLifecyclePanel";
import { buildPolicyRegisterCsv, dateStatusOf } from "../utils/policyRegisterCsv";
import { downloadBlob } from "../utils/downloadBlob";

const DATE_BADGE_CLS = {
    "Within date range":   "bg-green-50 text-green-700 border-green-200",
    "Review overdue":      "bg-red-50 text-red-700 border-red-200",
    "Expired":             "bg-red-50 text-red-700 border-red-200",
    "Not yet effective":   "bg-indigo-50 text-indigo-700 border-indigo-200",
    "Revision in progress":"bg-amber-50 text-amber-700 border-amber-200",
    "Not published":       "bg-gray-100 text-gray-500 border-gray-200",
    "Archived":            "bg-gray-100 text-gray-400 border-gray-200",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// Policy register (prototype tab 1): every policy with its lifecycle at a glance — search, status
// filter, effective / review / expiration dates with the date-status badge, attestation progress,
// live alerts, CSV export, and the expandable lifecycle panel.
const PolicyRegister = ({ policyDocs, dichotomyAlerts, attestationSummary, titleOf, apiCall, canManage, canAdmin, currentUserId, onChanged, onGoToGenerate, openPolicy }) => {
    const [statusFilter, setStatusFilter] = useState("all");
    const [showArchived, setShowArchived] = useState(false);
    const [search, setSearch] = useState("");
    const [expandedId, setExpandedId] = useState(openPolicy?.id || null);

    const q = search.trim().toLowerCase();
    const rows = policyDocs
        .filter(d => showArchived || d.lifecycleStatus !== "Archived")
        .filter(d => statusFilter === "all" || d.lifecycleStatus === statusFilter)
        .filter(d => !q || `${titleOf(d)} ${d.ownerName || ""} ${d.id} ${d.category || ""}`.toLowerCase().includes(q));

    const attOf = (id) => attestationSummary?.find(a => a.id === id);
    const alertsOf = (d) => (dichotomyAlerts || []).filter(a => a.newUploadId === d.id && !a.isResolved).length;
    const now = new Date();

    const exportCsv = () => {
        const csv = buildPolicyRegisterCsv(rows, { titleOf, now });
        downloadBlob(new Blob([csv], { type: "text/csv" }), "axaris-policy-register.csv");
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search policies, owners…"
                    className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 w-48" />
                {["all", ...Object.keys(POLICY_STATUS_CFG).filter(s => s !== "Discarded" && (showArchived || s !== "Archived"))].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${statusFilter === s ? "bg-indigo-600 text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"}`}>
                        {s === "all" ? "All statuses" : POLICY_STATUS_CFG[s].label}
                    </button>
                ))}
                <label className="ml-auto text-xs text-gray-500 flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={showArchived} onChange={e => { setShowArchived(e.target.checked); if (!e.target.checked && statusFilter === "Archived") setStatusFilter("all"); }} />
                    Show archived
                </label>
                <button onClick={exportCsv} className="text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium">Export register</button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-200">
                                <th className="px-3 py-2 font-semibold">Policy / version</th>
                                <th className="px-3 py-2 font-semibold">Lifecycle</th>
                                <th className="px-3 py-2 font-semibold">Owner</th>
                                <th className="px-3 py-2 font-semibold">Effective</th>
                                <th className="px-3 py-2 font-semibold">Review / expiration</th>
                                <th className="px-3 py-2 font-semibold">Attestation</th>
                                <th className="px-3 py-2 font-semibold">Alerts</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(d => {
                                const cfg = POLICY_STATUS_CFG[d.lifecycleStatus] || POLICY_STATUS_CFG.Draft;
                                const att = attOf(d.id);
                                const alerts = alertsOf(d);
                                const isOpen = expandedId === d.id;
                                const dateStatus = dateStatusOf(d, now);
                                return (
                                    <React.Fragment key={d.id}>
                                        <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(isOpen ? null : d.id)}>
                                            <td className="px-3 py-2 text-gray-800 font-medium max-w-[260px]">
                                                <div className="truncate">{titleOf(d)}</div>
                                                <div className="text-[10px] text-gray-400 font-normal">#{d.id} · v{d.version} {d.publishedAt ? "published" : "draft"}{d.inForceVersionId ? " · earlier version in force" : ""}</div>
                                            </td>
                                            <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.cls}`}>{cfg.label}</span></td>
                                            <td className="px-3 py-2 text-gray-600">{d.ownerName || "—"}</td>
                                            <td className="px-3 py-2 text-gray-600">{fmt(d.effectiveDate)}</td>
                                            <td className="px-3 py-2 text-gray-600">
                                                <div>{fmt(d.nextReviewDate)} / {fmt(d.expirationDate)}</div>
                                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${DATE_BADGE_CLS[dateStatus] || DATE_BADGE_CLS["Not published"]}`}>{dateStatus}</span>
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                                {att ? `${att.attestedAssignedCount ?? 0} / ${att.assignedCount ?? 0} assigned · ${att.attestedCount} total` : "—"}
                                            </td>
                                            <td className="px-3 py-2">
                                                {alerts > 0
                                                    ? <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200">⚠ {alerts} live</span>
                                                    : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-gray-400">{isOpen ? "▾" : "▸"}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="border-b border-gray-100">
                                                <td colSpan={8} className="px-3 pb-3">
                                                    <PolicyLifecyclePanel policy={d} title={titleOf(d)} apiCall={apiCall} canManage={canManage} canAdmin={canAdmin}
                                                        currentUserId={currentUserId} onChanged={onChanged} initialTab={openPolicy?.id === d.id ? openPolicy.tab : "actions"} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">
                                    {policyDocs.length === 0
                                        ? <>No policies yet. <button onClick={onGoToGenerate} className="text-indigo-700 underline">Generate a policy</button> or upload documents on the Documents tab.</>
                                        : "No policies match this filter."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PolicyRegister;
